import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config/api';
import {
  SimulatorSession,
  SimulatorStopResult,
  SimulatorCctv,
  SimulatorMap,
  SimulatorStartResponse,
  SimulatorStopResponse,
  SimulatorStatusResponse,
  SimulatorCctvListResponse,
  SimulatorMapListResponse,
  SimulatorEmergencyBell,
  SimulatorEmergencyBellListResponse,
} from '../models/Simulator';

/**
 * Simulator — manage-backend 시뮬레이터 세션 제어 + 맵별 CCTV 목록 조회.
 * 응답 envelope 풀이 패턴은 `RuntimeService.ts` 와 동일.
 */
export class SimulatorService {
  static async start(projectId: number, mapId: number): Promise<SimulatorSession> {
    try {
      const response = await apiClient.post<SimulatorStartResponse>(
        API_ENDPOINTS.SIMULATOR.START,
        { project_id: projectId, map_id: mapId },
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || '시뮬레이터 시작 실패');
    } catch (err) {
      // backend 가 mediamtx 기동 실패 등으로 HTTP 500 + envelope
      // `{success:false, message, data: <error session with error_message>}`
      // 를 돌려주는 경우 — axios 가 reject 하므로 envelope 의 error session
      // 을 꺼내 정상 반환해 UI 가 status='error' + error_message 를 표시하게 한다.
      const envelope = (err as { response?: { data?: SimulatorStartResponse } })
        .response?.data;
      const errSession = envelope?.data;
      if (errSession && errSession.error_message) {
        return errSession;
      }
      throw err instanceof Error
        ? err
        : new Error(envelope?.message || '시뮬레이터 시작 실패');
    }
  }

  static async stop(sessionId: string): Promise<SimulatorStopResult> {
    const response = await apiClient.post<SimulatorStopResponse>(
      API_ENDPOINTS.SIMULATOR.STOP,
      { session_id: sessionId },
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || '시뮬레이터 중지 실패');
  }

  /**
   * 현재 세션 status 조회. `session_id` 또는 `project_id` 중 하나로 조회.
   * 세션이 없으면 backend 가 `data: null, success: true` 로 응답한다.
   */
  static async status(params: { sessionId?: string; projectId?: number }): Promise<SimulatorSession | null> {
    const query = new URLSearchParams();
    if (params.sessionId) {
      query.append('session_id', params.sessionId);
    }
    if (params.projectId !== undefined) {
      query.append('project_id', String(params.projectId));
    }
    const qs = query.toString();
    const url = qs
      ? `${API_ENDPOINTS.SIMULATOR.STATUS}?${qs}`
      : API_ENDPOINTS.SIMULATOR.STATUS;

    const response = await apiClient.get<SimulatorStatusResponse>(url);
    if (response.data.success) {
      return response.data.data ?? null;
    }
    throw new Error(response.data.message || '시뮬레이터 상태 조회 실패');
  }

  /** 선택된 맵의 CCTV 목록 — MapData.cctvs.lightCctvs + site_network_devices IP join. */
  static async listCctvs(mapId: number): Promise<SimulatorCctv[]> {
    const response = await apiClient.get<SimulatorCctvListResponse>(
      API_ENDPOINTS.SIMULATOR.CCTVS(mapId),
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    if (response.data.success && !response.data.data) {
      return [];
    }
    throw new Error(response.data.message || 'CCTV 목록 조회 실패');
  }

  /**
   * 선택된 맵의 비상벨 목록 — MapData export 의 EmergencyBell 버킷 기반.
   * 빈 맵은 success + data 없음(omitempty) 이라 [] 로 취급.
   */
  static async listEmergencyBells(mapId: number): Promise<SimulatorEmergencyBell[]> {
    const response = await apiClient.get<SimulatorEmergencyBellListResponse>(
      API_ENDPOINTS.SIMULATOR.EMERGENCY_BELLS(mapId),
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    if (response.data.success && !response.data.data) {
      return [];
    }
    throw new Error(response.data.message || '비상벨 목록 조회 실패');
  }

  /** 사이트 프로젝트의 맵 목록 — 시뮬레이터 맵 드롭다운에서 사용. */
  static async listMaps(siteProjectId: number): Promise<SimulatorMap[]> {
    const response = await apiClient.get<SimulatorMapListResponse>(
      API_ENDPOINTS.MAP_EDITOR.LIST_PROJECTS(siteProjectId),
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    if (response.data.success && !response.data.data) {
      return [];
    }
    throw new Error(response.data.message || '맵 목록 조회 실패');
  }
}
